import { memo } from 'react'
import { Minus, X } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { getEffectiveResponse, getRemovalRule, schedulingRules } from '../schedulingRules'
import type { Job, Worker } from '../types'
import { getVisibleJobWorkers } from '../utils/job-workers'
import type { JobDisplayStatus } from '../utils/job-status'
import { formatCompactDuration, formatDateKeyLabel, formatTimeRangeInZone, toDateKey } from '../utils/time'
import { AssignedWorkerRow } from './AssignedWorkerRow'

export interface MapJobPopupProps {
  job: Job
  display: JobDisplayStatus | undefined
  workersById: Record<string, Worker>
  timeZone: string
  now: number
  canForceRemove: boolean
  minimized: boolean
  hoveredWorkerId: string | null
  onMinimizedChange: (minimized: boolean) => void
  /** Clears the selection. */
  onClose: () => void
  onRemoveWorker: (jobId: string, workerId: string) => void
  onHoverWorker: (workerId: string | null) => void
}

/**
 * Small job summary over the map's top-left corner (below the layer toggles), replacing the old
 * InfoWindow. Minimize collapses it to a pill so the whole map is visible. Desktop/tablet only;
 * on mobile the bottom sheet card shows the same information.
 */
export const MapJobPopup = memo(function MapJobPopup({
  job,
  display,
  workersById,
  timeZone,
  now,
  canForceRemove,
  minimized,
  hoveredWorkerId,
  onMinimizedChange,
  onClose,
  onRemoveWorker,
  onHoverWorker,
}: MapJobPopupProps) {
  const position = { top: 52, left: schedulingRules.popupInsetPx }

  if (minimized) {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onMinimizedChange(false)
        }}
        aria-label={`Show details for ${job.bookingNo}`}
        style={position}
        className="absolute z-20 inline-flex items-center gap-1.5 rounded-full border border-[#169b62] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#20352b] shadow-md hover:bg-[#f3faf6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
      >
        <span className="h-2 w-2 rounded-full bg-[#169b62]" aria-hidden="true" />
        {job.bookingNo}
      </button>
    )
  }

  const isOtherDay = toDateKey(job.scheduledStart, timeZone) !== toDateKey(now, timeZone)
  const when = `${isOtherDay ? `${formatDateKeyLabel(toDateKey(job.scheduledStart, timeZone))} · ` : ''}${formatTimeRangeInZone(job.scheduledStart, job.scheduledEnd, timeZone)}`
  const lateBy = display?.lateMinutes != null && display.lateMinutes > 0 ? `${formatCompactDuration(display.lateMinutes)} late` : null
  const workers = getVisibleJobWorkers(job, now)

  return (
    <section
      aria-label={`${job.bookingNo} details`}
      onClick={(event) => event.stopPropagation()}
      style={{ ...position, width: schedulingRules.popupWidthPx, maxHeight: 'calc(100% - 52px - 104px)' }}
      className="absolute z-20 flex flex-col overflow-hidden rounded-lg border border-[#dfeae3] bg-white text-left shadow-lg"
    >
      <header className="flex items-center gap-1 border-b border-[#eef3ef] py-1 pl-2.5 pr-1">
        <h3 className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[#20352b]">{job.bookingNo}</h3>
        <button
          type="button"
          onClick={() => onMinimizedChange(true)}
          aria-label="Minimize job details"
          className="flex h-6 w-6 items-center justify-center rounded text-[#718078] hover:bg-[#eef6f1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close and clear the job selection"
          className="flex h-6 w-6 items-center justify-center rounded text-[#718078] hover:bg-[#eef6f1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </header>
      <div className="min-h-0 overflow-y-auto px-2.5 py-1.5">
        <p className="truncate text-[11px] font-medium text-[#355445]" title={job.service}>{job.service}</p>
        <p className="truncate text-[11px] text-[#355445]">
          <span className="tabular-nums">{when}</span>
          {lateBy ? <span className="font-semibold text-red-600">{` · ${lateBy}`}</span> : null}
        </p>
        <p className={cn('mt-0.5 line-clamp-2 text-[11px]', job.address ? 'text-[#718078]' : 'italic text-[#8aa093]')}>
          {job.address || (job.area ? `Area: ${job.area}` : 'No address on file')}
        </p>
        <div className="mt-1.5 border-t border-[#eef3ef] pt-1">
          {job.staffingPending ? (
            <div className="flex items-center gap-1.5 py-1" aria-busy="true" aria-label="Loading assigned workers">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3 flex-1" />
            </div>
          ) : workers.length > 0 ? (
            <ul aria-label="Assigned workers" className="-mx-1 space-y-0.5">
              {workers.map((jobWorker) => (
                <AssignedWorkerRow
                  key={jobWorker.workerId}
                  variant="popup"
                  job={job}
                  jobWorker={jobWorker}
                  worker={workersById[jobWorker.workerId] ?? null}
                  response={getEffectiveResponse(jobWorker, now)}
                  removal={getRemovalRule(job, jobWorker, { canForceRemove })}
                  now={now}
                  isHovered={hoveredWorkerId === jobWorker.workerId}
                  onHover={onHoverWorker}
                  onRemove={onRemoveWorker}
                />
              ))}
            </ul>
          ) : (
            <p className="py-0.5 text-[11px] text-[#718078]">
              <span className="font-medium text-[#20352b]">No worker assigned.</span> Add one from nearby workers below.
            </p>
          )}
        </div>
      </div>
    </section>
  )
})
